/**
 * MASTER B2B SCRIPT (DEMO-PROOF / PRODUCTION FRIENDLY)
 * POST /apps/inventory  { action: "..." }
 *
 * Modes:
 *  - Agent mode: localStorage agent_active_location_id + agent_active_stock_code
 *  - Customer mode: window.customerShipmentSite
 *  - Fallback: unlock buttons
 *
 * Key fixes:
 *  - In-flight guard to stop flicker/racing requests
 *  - Price patch uses .b2b-price-display (added in card template)
 *  - ✅ Adds ONE global delegated AJAX Add-to-Cart handler (fixes “click does nothing”)
 *  - ✅ Respects btn.dataset.busy so inventory checks don’t fight “Adding…”
 */

(function () {
  const PROXY_PATH = "/apps/inventory";

  // ---------- helpers ----------
  const postJSON = async (url, payload) => {
    const res = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const text = await res.text().catch(() => "");
    let data = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch (_) {
      if ((text || "").trim().startsWith("<")) {
        const err = new Error(
          "Proxy returned HTML (likely login redirect or theme rendered). Ensure App Proxy is configured and /apps/inventory is allowed."
        );
        err.status = res.status;
        err.raw = text.slice(0, 400);
        throw err;
      }
    }

    if (!res.ok) {
      const msg = data?.error || `Request failed (${res.status})`;
      const err = new Error(msg);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  };

  const uniq = (arr) => [...new Set((arr || []).filter(Boolean))];

  const normalizeGid = (maybeGidOrNumber, resourceName) => {
    if (!maybeGidOrNumber) return "";
    const s = String(maybeGidOrNumber).trim();
    if (!s) return "";
    if (s.startsWith("gid://")) return s;
    const digits = s.match(/\d+$/)?.[0];
    if (!digits) return "";
    return `gid://shopify/${resourceName}/${digits}`;
  };

  const normalizeStockCode = (v) => String(v || "").trim();

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());

  const moneyFmt = (amount, currencyCode) => {
    const n = Number(amount);
    if (!isFinite(n)) return "";
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currencyCode || "USD",
      }).format(n);
    } catch {
      return `$${n.toFixed(2)}`;
    }
  };

  const getCells = () => document.querySelectorAll(".b2b-inventory-cell");

  const safeText = (v, max = 40) => {
    const s = String(v || "").trim().replace(/\s+/g, " ");
    if (!s) return "";
    return s.length > max ? s.slice(0, max) : s;
  };

  const readAgentIdentity = () => {
    const firstName =
      safeText(window.agentFirstName, 40) ||
      safeText(window.customerFirstName, 40) ||
      safeText(localStorage.getItem("agent_first_name"), 40) ||
      "";

    const lastName =
      safeText(window.agentLastName, 40) ||
      safeText(window.customerLastName, 40) ||
      safeText(localStorage.getItem("agent_last_name"), 40) ||
      "";

    return { firstName, lastName };
  };

  // ---------- context ----------
  const readAgentContext = () => {
    const rawLoc = localStorage.getItem("agent_active_location_id");
    const rawCode = localStorage.getItem("agent_active_stock_code");
    const rawEmail = window.agentEmail || localStorage.getItem("agent_email") || "";

    const locationId = normalizeGid(rawLoc, "CompanyLocation");
    const stockCode = normalizeStockCode(rawCode);
    const agentEmail = String(rawEmail || "").trim();

    const isAgentMode = !!locationId && !!stockCode;
    return { isAgentMode, locationId, stockCode, agentEmail };
  };

  const readCustomerContext = () => {
    const shipmentSite = window.customerShipmentSite
      ? String(window.customerShipmentSite).trim()
      : "";
    return { isCustomerMode: !!shipmentSite, shipmentSite };
  };

  // ---------- UI ----------
  function setBtnState(cell, enabled, label) {
    const btn = cell.querySelector('button[name="add"]');
    if (!btn) return;

    // If the button is currently in an "Adding..." state, don’t fight it.
    if (btn.dataset.busy === "1") return;

    btn.disabled = !enabled;
    btn.innerText = label || (enabled ? "Add to Cart" : "Out of Stock");
    btn.classList.toggle("disabled", !enabled);
    btn.classList.remove("loading");
  }

  function lockAllButtonsLoading() {
    document
      .querySelectorAll('.b2b-inventory-cell button[name="add"]')
      .forEach((btn) => {
        if (btn.dataset.busy === "1") return;
        btn.disabled = true;
        btn.innerText = "Loading Stock...";
        btn.classList.add("loading");
        btn.classList.remove("disabled");
      });
  }

  function unlockAllButtons() {
    document
      .querySelectorAll('.b2b-inventory-cell button[name="add"]')
      .forEach((btn) => {
        if (btn.dataset.busy === "1") return;
        btn.disabled = false;
        btn.innerText = "Add to Cart";
        btn.classList.remove("disabled", "loading");
      });
  }

  // ---------- in-flight guard (prevents flicker) ----------
  let runToken = 0;

  // ---------- CUSTOMER MODE ----------
  async function runCustomerLogic(shipmentSite) {
    const myToken = ++runToken;
    const cells = getCells();
    if (!cells.length) return;

    const handles = uniq(Array.from(cells).map((c) => c.dataset.handle));
    if (!handles.length) return;

    lockAllButtonsLoading();

    try {
      const data = await postJSON(PROXY_PATH, {
        action: "inventory",
        handles,
        stockSourceCode: shipmentSite,
      });

      if (myToken !== runToken) return; // stale response

      const map = new Map();
      if (data?.data?.products?.edges) {
        data.data.products.edges.forEach((e) => map.set(e.node.handle, e.node));
      }

      cells.forEach((cell) => {
        const handle = cell.dataset.handle;
        const variantId = String(cell.dataset.variantId || "");
        const productNode = map.get(handle);

        let availableQty = 0;

        if (productNode?.variants?.edges?.length) {
          const variantEdge = productNode.variants.edges.find((v) =>
            String(v.node.id).endsWith(`/${variantId}`)
          );
          const variant = variantEdge?.node;

          // Backend already flattens inventoryLevel with fallback
          availableQty = Number(
            variant?.inventoryItem?.inventoryLevel?.quantities?.[0]?.quantity || 0
          );
        }

        setBtnState(
          cell,
          availableQty > 0,
          availableQty > 0 ? "Add to Cart" : "Out of Stock"
        );
      });
    } catch (err) {
      console.error("[B2B] Customer inventory failed:", err);
      unlockAllButtons(); // fail open
    }
  }

  // ---------- AGENT MODE ----------
  async function runAgentLogic(locationId, stockCode) {
    const myToken = ++runToken;

    const cells = getCells();

    // Cart page has no cells — hijack must still work.
    setupCheckoutHijack();

    if (!cells.length) return;

    const handles = uniq(Array.from(cells).map((c) => c.dataset.handle));
    if (!handles.length) return;

    lockAllButtonsLoading();

    const BATCH_SIZE = 50;
    for (let i = 0; i < handles.length; i += BATCH_SIZE) {
      const batchHandles = handles.slice(i, i + BATCH_SIZE);

      try {
        const data = await postJSON(PROXY_PATH, {
          action: "get-b2b-context",
          handles: batchHandles,
          companyLocationId: locationId,
          stockSourceCode: stockCode,
        });

        if (myToken !== runToken) return; // stale run

        const map = new Map();
        if (data?.data?.products?.edges) {
          data.data.products.edges.forEach((e) => map.set(e.node.handle, e.node));
        }

        cells.forEach((cell) => {
          const handle = cell.dataset.handle;
          if (!batchHandles.includes(handle)) return;

          const variantId = String(cell.dataset.variantId || "");
          const productNode = map.get(handle);

          if (!productNode) {
            setBtnState(cell, false, "Out of Stock");
            return;
          }

          const variantEdge = productNode.variants.edges.find((v) =>
            String(v.node.id).endsWith(`/${variantId}`)
          );
          const variant = variantEdge?.node;

          if (!variant) {
            setBtnState(cell, false, "Out of Stock");
            return;
          }

          // Price patch
          const p = variant.contextualPricing?.price;
          if (p?.amount && p?.currencyCode) {
            const priceEl =
              cell.closest("tr")?.querySelector(".b2b-price-display") ||
              cell.querySelector(".b2b-price-display");

            if (priceEl) {
              priceEl.innerText = moneyFmt(p.amount, p.currencyCode);
              priceEl.classList.add("b2b-patched");
            }
          }

          // Stock (flattened by backend with fallback)
          const qty = Number(
            variant.inventoryItem?.inventoryLevel?.quantities?.[0]?.quantity || 0
          );

          setBtnState(cell, qty > 0, qty > 0 ? "Add to Cart" : "Out of Stock");
        });
      } catch (err) {
        console.error("[B2B] Agent batch failed:", err);
        cells.forEach((cell) => {
          if (batchHandles.includes(cell.dataset.handle)) {
            setBtnState(cell, false, "Out of Stock");
          }
        });
      }
    }
  }

  // ---------- Checkout hijack (agent mode only) ----------
  function setupCheckoutHijack() {
    const agentNow = readAgentContext();
    if (!agentNow.isAgentMode) return;

    const selectors =
      'button[name="checkout"], .cart__checkout-button, [href="/checkout"], form[action="/cart"] [type="submit"]';
    const checkoutBtns = document.querySelectorAll(selectors);
    if (!checkoutBtns.length) return;

    checkoutBtns.forEach((btn) => {
      if (btn.dataset.hijacked === "true") return;

      const newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);

      newBtn.dataset.hijacked = "true";
      newBtn.classList.add("agent-checkout-active");

      newBtn.innerText = "Place B2B Order";
      newBtn.style.backgroundColor = "#008060";
      newBtn.style.fontSize = "13px";

      newBtn.addEventListener("click", async (e) => {
        const agent = readAgentContext();
        e.preventDefault();
        e.stopPropagation();

        if (!agent.isAgentMode) {
          window.location.href = "/checkout";
          return;
        }

        newBtn.innerText = "Processing...";
        newBtn.disabled = true;

        try {
          if (!isValidEmail(agent.agentEmail)) {
            throw new Error(
              "Missing agent email. Ensure your portal sets localStorage agent_email."
            );
          }

          const cart = await fetch("/cart.js", { credentials: "same-origin" }).then((r) =>
            r.json()
          );
          if (!cart || cart.item_count === 0) throw new Error("Cart is empty");

          let clientName = localStorage.getItem("agent_company_name") || "";
          let clientAddr = null;
          try {
            clientAddr = JSON.parse(
              localStorage.getItem("agent_company_address") || "null"
            );
          } catch {}

          if (!clientName || !clientAddr) {
            newBtn.innerText = "Fetching Client...";
            const companyData = await postJSON(PROXY_PATH, {
              action: "get-agent-companies",
              agentEmail: agent.agentEmail,
            });

            const norm = (v) => String(v || "").trim();
            const targetLoc = norm(agent.locationId);

            const match = (companyData.companies || []).find(
              (c) => norm(c.locationId) === targetLoc
            );

            if (!match) {
              throw new Error("Could not find company details. Please re-select client.");
            }

            clientName = match.companyName || match.displayName || match.name || "";
            clientAddr = match.address || {};

            localStorage.setItem("agent_company_name", clientName);
            localStorage.setItem("agent_company_address", JSON.stringify(clientAddr));
          }

          const agentDetails = readAgentIdentity();

          newBtn.innerText = "Generating Invoice...";

          const data = await postJSON(PROXY_PATH, {
            action: "create-draft-order",
            companyLocationId: agent.locationId,
            items: cart.items,
            agentEmail: agent.agentEmail,
            agentDetails,
            clientDetails: {
              name: clientName,
              address: clientAddr,
              stockCode: agent.stockCode,
            },
          });

          if (data?.invoiceUrl) {
            window.location.href = data.invoiceUrl;
            return;
          }

          throw new Error(data?.error || "Order Creation Failed");
        } catch (err) {
          console.error("Order Failed:", err);
          alert("Order Failed: " + (err.message || "Unknown error"));
          newBtn.disabled = false;
          newBtn.innerText = "Place B2B Order";
        }
      });
    });
  }

  // ---------- Add to cart (single delegated handler) ----------
  function setupAddToCartHandler() {
    if (window.__b2bAddToCartBound) return;
    window.__b2bAddToCartBound = true;

    document.addEventListener(
      "click",
      async (e) => {
        const btn = e.target.closest(".add-to-cart-ajax");
        if (!btn) return;

        // Intercept early + prevent other theme scripts from swallowing it
        e.preventDefault();
        e.stopPropagation();

        if (btn.disabled) return;

        const productId = btn.getAttribute("data-product-id");
        if (!productId) return;

        const form = document.getElementById(`card-product-form-${productId}`);
        if (!form) {
          console.warn("[B2B] Missing form for product:", productId);
          return;
        }

        const idInput = form.querySelector('input[name="id"]');
        const qtyInput =
          form.querySelector('input[name="quantity"]') ||
          document.getElementById(`Quantity-${productId}`);

        const variantId = idInput ? String(idInput.value || "").trim() : "";
        const quantity = qtyInput ? Number(qtyInput.value || 1) : 1;

        if (!variantId || !Number.isFinite(quantity) || quantity <= 0) return;

        // UI lock (and stop inventory check from fighting this button)
        btn.dataset.busy = "1";
        const originalLabel = btn.innerText || "Add to Cart";
        btn.disabled = true;
        btn.innerText = "Adding...";

        const msgBox = document.getElementById(`add-message-${productId}`);
        if (msgBox) msgBox.style.display = "none";

        try {
          const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: "POST",
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              items: [{ id: variantId, quantity }],
            }),
          });

          const data = await res.json().catch(() => ({}));

          if (!res.ok) {
            const message =
              data?.description ||
              data?.message ||
              data?.error ||
              `Could not add item (HTTP ${res.status})`;
            throw new Error(message);
          }

          btn.innerText = "Added!";

          // Update cart bubble (best-effort)
          try {
            const cart = await fetch(`${window.Shopify.routes.root}cart.js`, {
              credentials: "same-origin",
            }).then((r) => r.json());

            document
              .querySelectorAll('.cart-count-bubble span[aria-hidden="true"]')
              .forEach((el) => (el.textContent = cart.item_count));
          } catch (_) {}

          setTimeout(() => {
            btn.dataset.busy = "0";
            btn.disabled = false;
            btn.innerText = originalLabel;
          }, 1200);
        } catch (err) {
          console.error("[B2B] Add to cart failed:", err);

          if (msgBox) {
            msgBox.textContent = err.message || "Could not add item.";
            msgBox.style.display = "block";
          }

          btn.dataset.busy = "0";
          btn.disabled = false;
          btn.innerText = originalLabel;
        }
      },
      true // capture: intercept before other theme handlers
    );
  }

  function observeCartRerenders() {
    try {
      const mo = new MutationObserver(() => {
        setupCheckoutHijack();
      });
      mo.observe(document.documentElement, { childList: true, subtree: true });
    } catch (_) {}
  }

  // ---------- entrypoint ----------
  function runB2BInventoryCheck() {
    const agent = readAgentContext();
    const customer = readCustomerContext();

    if (agent.isAgentMode) setupCheckoutHijack();

    if (agent.isAgentMode) return runAgentLogic(agent.locationId, agent.stockCode);
    if (customer.isCustomerMode) return runCustomerLogic(customer.shipmentSite);

    return unlockAllButtons();
  }

  document.addEventListener("DOMContentLoaded", () => {
    window.runB2BInventoryCheck = runB2BInventoryCheck;

    // ✅ required so Add-to-Cart works (your card button is type="button")
    setupAddToCartHandler();

    observeCartRerenders();
    runB2BInventoryCheck();
  });
})();